import React, { useEffect, useState } from 'react';
import { adminNotificationsApi, handleNotificationError } from '../../utils/notificationsApi';
import { formatDistanceToNow } from 'date-fns';
import { tr } from 'date-fns/locale';
import PageHeader from '../components/shared/PageHeader';
import { triggerNotificationRefresh } from '../../utils/notificationsApi';

const TYPE_CONFIG = {
  INFO: { icon: 'fa-info-circle', color: '#17a2b8', bg: 'rgba(23,162,184,0.08)' },
  WARNING: { icon: 'fa-exclamation-triangle', color: '#ffc107', bg: 'rgba(255,193,7,0.08)' },
  ERROR: { icon: 'fa-times-circle', color: '#dc3545', bg: 'rgba(220,53,69,0.08)' },
  SUCCESS: { icon: 'fa-check-circle', color: '#28a745', bg: 'rgba(40,167,69,0.08)' },
  DELIVERY_FAILED: { icon: 'fa-exclamation-circle', color: '#dc3545', bg: 'rgba(220,53,69,0.08)' },
  DELIVERY_RETURNED: { icon: 'fa-undo', color: '#e67e22', bg: 'rgba(230,126,34,0.08)' },
};

function AdminNotificationsPage() {
  const [notifications, setNotifications] = useState([]);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [filter, setFilter] = useState('all');
  const [showDeleteAllConfirm, setShowDeleteAllConfirm] = useState(false);

  const loadNotifications = async (pageNum = page, readFilter = filter) => {
    try {
      setLoading(true);
      const params = { page: pageNum, limit };
      if (readFilter === 'unread') params.isRead = false;
      if (readFilter === 'read') params.isRead = true;

      const queryString = new URLSearchParams(params).toString();
      const res = await adminNotificationsApi.list(pageNum, limit);
      if (res.success) {
        let data = res.data || res.notifications || [];
        if (readFilter === 'unread') data = data.filter(n => !n.isRead);
        if (readFilter === 'read') data = data.filter(n => n.isRead);
        setNotifications(data);
        setTotal(res.pagination?.total || 0);
        setPage(pageNum);
      }
    } catch (error) {
      handleNotificationError(error, 'Bildirimler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadNotifications(1, filter);
  }, [filter]);

  const handleMarkRead = async (id) => {
    try {
      setActionLoading(id);
      await adminNotificationsApi.markRead(id);
      setNotifications(prev => prev.map(n => (n.id === id ? { ...n, isRead: true } : n)));
      triggerNotificationRefresh();
    } catch (error) {
      handleNotificationError(error, 'Bildirim okunamadı');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (id) => {
    try {
      setActionLoading(id);
      await adminNotificationsApi.delete(id);
      setNotifications(prev => prev.filter(n => n.id !== id));
      setTotal(prev => prev - 1);
      triggerNotificationRefresh();
    } catch (error) {
      handleNotificationError(error, 'Bildirim silinemedi');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      setActionLoading('markAll');
      await adminNotificationsApi.markAllRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
      triggerNotificationRefresh();
    } catch (error) {
      handleNotificationError(error, 'Bildirimler okundu işaretlenemedi');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteAll = async () => {
    try {
      setActionLoading('deleteAll');
      await adminNotificationsApi.deleteAll();
      setNotifications([]);
      setTotal(0);
      setShowDeleteAllConfirm(false);
      triggerNotificationRefresh();
    } catch (error) {
      handleNotificationError(error, 'Bildirimler silinemedi');
    } finally {
      setActionLoading(null);
    }
  };

  const unreadCount = notifications.filter(n => !n.isRead).length;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const getTypeConfig = (type) => TYPE_CONFIG[type] || TYPE_CONFIG.INFO;

  return (
    <div className="container-fluid p-0">
      <PageHeader
        title="Bildirimler"
        description="Sistemdeki tüm bildirimleri buradan görüntüleyebilir ve yönetebilirsiniz."
        breadcrumbs={[{ label: 'Admin' }, { label: 'Bildirimler' }]}
        actions={
          <div className="d-flex flex-wrap gap-2">
            <button
              className="btn btn-white border"
              onClick={() => loadNotifications(1, filter)}
              disabled={loading}
            >
              <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''} me-2`}></i>
              Yenile
            </button>
            <button
              className="btn btn-white border"
              onClick={handleMarkAllRead}
              disabled={actionLoading === 'markAll' || loading || unreadCount === 0}
            >
              {actionLoading === 'markAll' ? (
                <><span className="spinner-border spinner-border-sm me-2"></span>İşleniyor...</>
              ) : (
                <><i className="fas fa-check-double me-2"></i>Tümünü Okundu Yap</>
              )}
            </button>
            <button
              className="btn btn-outline-danger"
              onClick={() => setShowDeleteAllConfirm(true)}
              disabled={loading || notifications.length === 0}
            >
              <i className="fas fa-trash-alt me-2"></i>Tümünü Sil
            </button>
          </div>
        }
      />

      {/* Delete All Confirmation */}
      {showDeleteAllConfirm && (
        <div className="alert alert-danger d-flex align-items-center justify-content-between mb-3" role="alert">
          <div>
            <i className="fas fa-exclamation-triangle me-2"></i>
            <strong>Tüm bildirimler silinecek.</strong> Bu işlem geri alınamaz.
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-sm btn-light" onClick={() => setShowDeleteAllConfirm(false)}>İptal</button>
            <button
              className="btn btn-sm btn-danger"
              onClick={handleDeleteAll}
              disabled={actionLoading === 'deleteAll'}
            >
              {actionLoading === 'deleteAll' ? (
                <><span className="spinner-border spinner-border-sm me-1"></span>Siliniyor...</>
              ) : 'Evet, Tümünü Sil'}
            </button>
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="d-flex align-items-center gap-2 mb-3">
        {[
          { key: 'all', label: 'Tümü', count: total },
          { key: 'unread', label: 'Okunmamış', count: unreadCount },
          { key: 'read', label: 'Okunmuş' },
        ].map(f => (
          <button
            key={f.key}
            className={`btn btn-sm ${filter === f.key ? 'btn-dark' : 'btn-white border'}`}
            onClick={() => setFilter(f.key)}
          >
            {f.label}
            {f.count > 0 && (
              <span className={`badge ms-2 ${filter === f.key ? 'bg-light text-dark' : 'bg-dark text-white'}`} style={{ fontSize: '0.7rem' }}>
                {f.count}
              </span>
            )}
          </button>
        ))}
        <div className="ms-auto text-muted small">
          Toplam {total} bildirim
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-secondary" role="status">
            <span className="visually-hidden">Yükleniyor...</span>
          </div>
          <div className="text-muted mt-2 small">Bildirimler yükleniyor...</div>
        </div>
      ) : notifications.length === 0 ? (
        <div className="card shadow-sm">
          <div className="card-body text-center py-5">
            <i className="fas fa-bell-slash fa-3x mb-3" style={{ color: '#ccc' }}></i>
            <h6 className="text-muted">
              {filter === 'unread' ? 'Okunmamış bildiriminiz yok' :
               filter === 'read' ? 'Okunmuş bildiriminiz yok' :
               'Henüz bildiriminiz bulunmuyor'}
            </h6>
          </div>
        </div>
      ) : (
        <div className="d-flex flex-column gap-2">
          {notifications.map(n => {
            const typeConf = getTypeConfig(n.type);
            const isProcessing = actionLoading === n.id;

            return (
              <div
                key={n.id}
                className="card shadow-sm border-0"
                style={{
                  borderLeft: `4px solid ${n.isRead ? '#dee2e6' : typeConf.color}`,
                  opacity: isProcessing ? 0.5 : 1,
                  transition: 'all 0.2s ease',
                  background: n.isRead ? '#fff' : typeConf.bg,
                }}
              >
                <div className="card-body py-3 px-4">
                  <div className="d-flex align-items-start gap-3">
                    {/* Icon */}
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                      style={{ width: 40, height: 40, backgroundColor: typeConf.bg }}
                    >
                      <i className={`fas ${typeConf.icon}`} style={{ color: typeConf.color, fontSize: '1rem' }}></i>
                    </div>

                    {/* Content */}
                    <div className="flex-grow-1 min-w-0">
                      <div className="d-flex align-items-start justify-content-between gap-2">
                        <div className="min-w-0">
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <span className={`fw-semibold ${n.isRead ? 'text-muted' : 'text-dark'}`} style={{ fontSize: '0.9rem' }}>
                              {n.title || 'Bildirim'}
                            </span>
                            {!n.isRead && (
                              <span className="badge rounded-pill" style={{ backgroundColor: typeConf.color, fontSize: '0.65rem', padding: '2px 8px' }}>
                                Yeni
                              </span>
                            )}
                          </div>
                          {n.message && (
                            <p className="mb-1 text-muted" style={{ fontSize: '0.85rem', lineHeight: 1.4 }}>{n.message}</p>
                          )}
                          <div className="d-flex align-items-center gap-3 mt-1" style={{ fontSize: '0.75rem' }}>
                            <span
                              className="text-muted"
                              title={n.createdAt ? new Date(n.createdAt).toLocaleString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : ''}
                            >
                              <i className="far fa-clock me-1"></i>
                              {n.createdAt ? formatDistanceToNow(new Date(n.createdAt), { addSuffix: true, locale: tr }) : '-'}
                            </span>
                            {n.metadata?.trigger && (
                              <span className="text-muted">
                                <i className="fas fa-cog me-1"></i>
                                {n.metadata.trigger}
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="d-flex gap-1 flex-shrink-0">
                          {!n.isRead && (
                            <button
                              className="btn btn-sm btn-light border-0"
                              onClick={() => handleMarkRead(n.id)}
                              disabled={isProcessing}
                              title="Okundu işaretle"
                              style={{ width: 32, height: 32, padding: 0 }}
                            >
                              <i className="fas fa-check text-success" style={{ fontSize: '0.8rem' }}></i>
                            </button>
                          )}
                          <button
                            className="btn btn-sm btn-light border-0"
                            onClick={() => handleDelete(n.id)}
                            disabled={isProcessing}
                            title="Sil"
                            style={{ width: 32, height: 32, padding: 0 }}
                          >
                            <i className="fas fa-times text-danger" style={{ fontSize: '0.8rem' }}></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="d-flex justify-content-center align-items-center gap-2 mt-4">
          <button
            className="btn btn-sm btn-white border"
            onClick={() => loadNotifications(page - 1, filter)}
            disabled={page <= 1 || loading}
          >
            <i className="fas fa-chevron-left me-1"></i> Önceki
          </button>
          <span className="text-muted small px-2">{page} / {totalPages}</span>
          <button
            className="btn btn-sm btn-white border"
            onClick={() => loadNotifications(page + 1, filter)}
            disabled={page >= totalPages || loading}
          >
            Sonraki <i className="fas fa-chevron-right ms-1"></i>
          </button>
        </div>
      )}
    </div>
  );
}

export default AdminNotificationsPage;
